/**
 * Rive layer for the Dragon Tiger benchmark port.
 *
 * Loads four artboards onto four stacked canvases — mirroring the four
 * `RiveWidgetController`s in
 *   packages/amgdragontiger_v2/lib/presentation/rive/rive_controller_manager.dart:
 *
 *     main    → dragontiger.riv       artboard "MainDragonTiger"   (cards, BetPanel, GirlAnim)
 *     popup   → dragontiger.riv       artboard "popupAnimation"    (StartBet / NoMoreBet popup)
 *     betting → dragontigerbettingui.riv artboard "MainDragonTiger" (bet-area highlights)
 *     winlose → MainWinLose.riv       artboard "Button"            (WIN/LOSE popup + amount)
 *
 * Why one Rive instance per canvas: `@rive-app/webgl2` clears the canvas at
 * the start of every frame, so two instances sharing a canvas overwrite each
 * other.
 *
 * Inputs are cached by name AND queried by `StateMachineInputType` (every
 * input in this runtime exposes both `value` and `fire()` — `fire()` is a
 * no-op on Number/Boolean — so type-checking via `.type` is the only safe
 * way to classify them).
 *
 * Nested inputs (e.g. `CardType` at path `DragonCard`) go through
 * `setNumberStateAtPath` / `setBooleanStateAtPath` / `fireStateAtPath`.
 */
import {
  Rive,
  Layout,
  Fit,
  Alignment,
  EventType,
  StateMachineInputType,
} from '@rive-app/webgl2';

import { debugLog, warn } from '../util/logger';
import { RiveEventNames, type RiveSide } from './riveInputs';

// ----------------------------------------------------------------------------
// Cached-input helpers (one of these per discovered SM input)
// ----------------------------------------------------------------------------

type AnyInput =
  | { type: 'number'; name: string; set(v: number): void; get(): number }
  | { type: 'boolean'; name: string; set(v: boolean): void; get(): boolean }
  | { type: 'trigger'; name: string; fire(): void };

/**
 * How the live .riv exposes its history-bar inputs, resolved once by silent
 * probing (see resolveHistoryScheme). `name`/`path` are templated by slot index
 * because only the trailing number changes between slots. A missing `path`
 * means the input sits at the top level rather than nested under an artboard.
 */
interface HistoryScheme {
  target: TargetKey;
  name: (index: number) => string;
  path?: (index: number) => string;
}

/**
 * Where a text run actually lives. `path` is the nested-artboard instance name
 * (e.g. min/max sit inside the `MinMax` nested artboard on MainDragonTiger, so
 * they need `setTextRunValueAtPath(name, value, 'MinMax')`); absent `path`
 * means a plain top-level run.
 */
interface TextRunPlacement {
  key: TargetKey;
  path?: string;
}

interface RiveTarget {
  /** Logical name: 'main' | 'popup' | 'betting' | 'winlose' */
  key: TargetKey;
  /** Underlying file (for logs). */
  file: string;
  /** Underlying state-machine name. */
  stateMachine: string;
  /** Live Rive instance. */
  instance: Rive;
  /** Name → cached input (only top-level inputs end up here). */
  inputs: Map<string, AnyInput>;
}

export type TargetKey = 'main' | 'popup' | 'betting' | 'winlose';

const FILES = {
  main: 'dragontiger.riv',
  popup: 'dragontiger.riv',
  betting: 'dragontigerbettingui.riv',
  winlose: 'MainWinLose.riv',
} as const;

const ARTBOARDS = {
  main: 'MainDragonTiger',
  popup: 'popupAnimation',
  betting: 'MainDragonTiger',
  winlose: 'Button',
} as const;

const STATE_MACHINES = {
  main: 'Main',
  popup: 'Main',
  betting: 'Main',
  winlose: 'animationManager',
} as const;

// ----------------------------------------------------------------------------
// Public types
// ----------------------------------------------------------------------------

export interface RiveLayerOptions {
  canvases: Record<TargetKey, HTMLCanvasElement>;
  assetBase?: string;
}

export type RiveEventTopic =
  | 'ready'
  | 'BetAreaClicked'
  | 'ChipClicked'
  | 'CardRevealed'
  | 'AnimationComplete'
  // Forwarded Flutter-side event names too — see _handleRiveEvent in
  // rive_controller_manager.dart:182-199.
  | 'placeBetClick'
  | 'evtClickChips'
  | 'evtClearClick'
  | 'evtButtonPlayClick'
  | 'UIButtonClick'
  | 'evtResultStop';

type RiveEventCallback = (data: Record<string, unknown>) => void;

// ----------------------------------------------------------------------------
// Implementation
// ----------------------------------------------------------------------------

export class RiveLayer {
  private readonly canvases: Record<TargetKey, HTMLCanvasElement>;
  private readonly assetBase: string;
  private readonly targets: Map<TargetKey, RiveTarget> = new Map();
  private readonly listeners: Map<string, Set<RiveEventCallback>> = new Map();
  /**
   * Resolved history-input scheme. `undefined`/`null` = not yet resolved.
   * We keep re-probing while unresolved (see RESOLVE_TRIES) because nested
   * inputs can be un-queryable for the first frame or two after load — locking
   * in that early miss would silently disable history forever.
   */
  private historyScheme: HistoryScheme | null | undefined;
  private historyTries = 0;
  /**
   * Text-run name → where it actually lives (top-level or nested under a path),
   * plus how many times we've probed. Resolved by silent probing and cached
   * once found; an empty result is NOT locked in until RESOLVE_TRIES probes,
   * for the same first-frame queryability reason as the history scheme above.
   */
  private readonly textRunTargets: Map<string, { placements: TextRunPlacement[]; tries: number }> =
    new Map();

  /** Max times a still-unresolved text run / history scheme is re-probed. */
  private static readonly RESOLVE_TRIES = 30;

  /**
   * Nested-artboard instance names to search when a text run isn't a top-level
   * run. `MinMax` holds the min/max table-limit runs on MainDragonTiger; the
   * rest are cheap extra guesses for other nested labels.
   */
  private static readonly TEXT_RUN_PATHS = ['MinMax', 'Table Limit', 'TableLimit', 'BetPanel'];

  constructor(opts: RiveLayerOptions) {
    this.canvases = opts.canvases;
    this.assetBase = opts.assetBase ?? '/assets/Rive/';
  }

  async init(): Promise<void> {
    this.fitAllCanvases();
    window.addEventListener('resize', () => this.fitAllCanvases());

    await Promise.all([
      this.loadOne('main'),
      this.loadOne('popup'),
      this.loadOne('betting'),
      this.loadOne('winlose'),
    ]);

    this.dumpInputs();
    // Keep the .riv's sound branch on so it always fires its sound-cue events
    // (FlipCard / StartCard / DragonTigerStart / DragonTigerWin, …). Actual
    // audio is played by SoundManager; the on-screen toggle gates that, not
    // this input — so we leave it true.
    this.setSoundOn(true);
    this.emit('ready', {});
  }

  private fitAllCanvases(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of Object.values(this.canvases)) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }
    for (const t of this.targets.values()) {
      try {
        t.instance.resizeDrawingSurfaceToCanvas();
      } catch {
        /* ignore */
      }
    }
  }

  private loadOne(key: TargetKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const file = FILES[key];
      const artboard = ARTBOARDS[key];
      const sm = STATE_MACHINES[key];
      const canvas = this.canvases[key];
      const src = `${this.assetBase}${file}`;
      const rive = new Rive({
        canvas,
        src,
        artboard,
        stateMachines: sm,
        autoplay: true,
        layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
        onLoad: () => {
          try {
            const inputs = this.cacheInputs(rive, sm);
            const target: RiveTarget = {
              key,
              file,
              stateMachine: sm,
              instance: rive,
              inputs,
            };
            this.targets.set(key, target);
            this.attachEventBridge(rive, key);
            try {
              rive.resizeDrawingSurfaceToCanvas();
            } catch {
              /* ignore */
            }
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onLoadError: (err: unknown) => {
          reject(new Error(`Failed to load ${src} [${artboard}/${sm}]: ${String(err)}`));
        },
      });
    });
  }

  private cacheInputs(rive: Rive, sm: string): Map<string, AnyInput> {
    const out = new Map<string, AnyInput>();
    let list: unknown[] = [];
    try {
      list = rive.stateMachineInputs(sm) ?? [];
    } catch (e) {
      warn('[Rive] stateMachineInputs failed for', sm, e);
      return out;
    }
    for (const raw of list) {
      const inp = raw as {
        name: string;
        type: number;
        value: number | boolean;
        fire(): void;
      };
      switch (inp.type) {
        case StateMachineInputType.Number:
          out.set(inp.name, {
            type: 'number',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Number(inp.value ?? 0),
          });
          break;
        case StateMachineInputType.Boolean:
          out.set(inp.name, {
            type: 'boolean',
            name: inp.name,
            set: (v) => {
              inp.value = v;
            },
            get: () => Boolean(inp.value),
          });
          break;
        case StateMachineInputType.Trigger:
          out.set(inp.name, {
            type: 'trigger',
            name: inp.name,
            fire: () => inp.fire(),
          });
          break;
        default:
          debugLog('[Rive] unknown input type', inp.type, 'for', inp.name);
      }
    }
    return out;
  }

  /** Dump every loaded artboard's inputs to the console for debugging. */
  private dumpInputs(): void {
    for (const t of this.targets.values()) {
      const rows = Array.from(t.inputs.values()).map((i) => ({
        target: t.key,
        file: t.file,
        sm: t.stateMachine,
        name: i.name,
        type: i.type,
      }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[Rive] ${t.key} (${t.file} / ${t.stateMachine}) — ${rows.length} input(s)`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  private attachEventBridge(rive: Rive, target: TargetKey): void {
    try {
      rive.on(EventType.RiveEvent, (event: unknown) => {
        const e = event as { data?: { name?: string; properties?: Record<string, unknown> } };
        const name = e?.data?.name;
        if (!name) return;
        const props = { ...(e?.data?.properties ?? {}), __target: target };
        // Forward known names; everything else is logged for discovery.
        if (this.listeners.has(name)) {
          this.emit(name, props);
        } else {
          debugLog('[Rive][event:passthrough]', target, name, props);
        }
      });
    } catch (e) {
      warn('[Rive] event bridge attach failed', e);
    }
  }

  // -----------------------------------------------------------------
  // Public high-level API — mirrors RiveControllerManager
  // -----------------------------------------------------------------

  /**
   * Set the BetPanel state — drives StartBet/NoMoreBet/Win/Tie popups.
   * Mirrors `_trySetBetPanel` calls in rive_controller_manager.dart:464-499.
   *
   *  state 1 (StartBet)   → popup
   *  state 2 (NoMoreBet)  → popup
   *  state 3 (DragonWin)  → main + popup
   *  state 4 (TigerWin)   → main + popup
   *  state 5 (Tie)        → main + popup
   */
  fireBetPanel(state: 1 | 2 | 3 | 4 | 5): void {
    const targets: TargetKey[] = state <= 2 ? ['popup'] : ['main', 'popup'];
    for (const t of targets) this.setNumberAny(t, 'BetPanel', state);
  }

  /** Plays the girl animation. 0=idle, 1=start game, 2=end game. */
  triggerGirlAnim(state: 0 | 1 | 2): void {
    if (state <= 0) return;
    this.setNumberAny('main', 'GirlAnim', state);
    this.fireTriggerAny('main', 'GirlAnimPlay');
  }

  /** No-op — matches Flutter's `clearAll()` which only logs. */
  triggerClearAll(): void {
    /* intentional no-op — kept for orchestrator symmetry */
  }

  /**
   * Set the Dragon and Tiger card values (1..52).
   * Mirrors rive_controller_manager.dart:398-428: input is `CardType`
   * at nested paths `DragonCard` and `TigerCard`.
   */
  setCards(dragon1to52: number, tiger1to52: number): void {
    this.setNumberAtPath('main', 'CardType', dragon1to52, 'DragonCard');
    this.setNumberAtPath('main', 'CardType', tiger1to52, 'TigerCard');
  }

  /**
   * Drive the card-reveal state machine.
   * Reveal-value semantics from rive_controller_manager.dart:431:
   *   1 = cards out
   *   2 = Tiger open
   *   3 = Dragon open
   *   4 = close
   *   5 = keep away
   *
   * In the live .riv the `Card` input may be exposed as either a Number
   * (Flutter set `.value`) or a Trigger (newer files). We handle both.
   */
  setCardRevealStep(step: 1 | 2 | 3 | 4 | 5): void {
    const t = this.targets.get('main');
    if (!t) return;
    const inp = t.inputs.get('Card');
    if (inp?.type === 'number') {
      inp.set(step);
      return;
    }
    if (inp?.type === 'trigger') {
      inp.fire();
      return;
    }
    // Fallback to the OLD trigger name.
    const legacy = t.inputs.get('trigCard');
    if (legacy?.type === 'trigger') legacy.fire();
    else debugLog('[Rive] no Card input found on main');
  }

  /**
   * Highlight the winning bet zone on the betting artboard.
   * The current .riv may only expose a single multi-state input — we try
   * the named-boolean approach first (Flutter rive_inputs.dart) then fall
   * back to a 1=Dragon / 2=Tiger / 3=Tie number, then nested paths.
   */
  setHighlight(side: RiveSide, on: boolean): void {
    const name =
      side === 'dragon'
        ? 'HighlightDragon'
        : side === 'tiger'
        ? 'HighlightTiger'
        : 'HighlightTie';
    if (!this.setBoolAny('betting', name, on)) {
      // Single-input fallback used in newer builds.
      const code = side === 'dragon' ? 1 : side === 'tiger' ? 2 : 3;
      this.setNumberAny('betting', 'Highlight', on ? code : 0);
    }
  }

  clearAllHighlights(): void {
    this.setHighlight('dragon', false);
    this.setHighlight('tiger', false);
    this.setHighlight('tie', false);
  }

  /**
   * Show the WIN/LOSE popup with an amount.
   * The amount text run lives on the WinLose artboard — set via the Rive
   * instance's `setTextRunValue`, NOT via the state machine
   * (rive_controller_manager.dart:581).
   *
   * Updated MainWinLose.riv contract: set the `YouWinLose` number (1=Win,
   * 2=Lose), then fire the `YouWinLoseTrigger` trigger to play the popup.
   * (`isSoundBetPanel` is no longer used.)
   */
  showWinLose(isWin: boolean, formattedAmount: string): void {
    const winlose = this.targets.get('winlose');
    if (!winlose) return;
    // Amount text run lives on the WinLose artboard. Probe silently first so a
    // missing run doesn't trigger the runtime's console warning.
    if (this.hasTextRun('winlose', 'txtWonAmount')) {
      winlose.instance.setTextRunValue('txtWonAmount', formattedAmount);
    }
    this.setNumberAny('winlose', 'YouWinLose', isWin ? 1 : 2);
    this.fireTriggerAny('winlose', 'YouWinLoseTrigger');
  }

  /**
   * Set a text run on every artboard that actually exposes it.
   *
   * The web runtime's `setTextRunValue` routes through `retrieveTextRun`, which
   * `console.warn`s ("Could not access a text run…") on every miss — so the old
   * try/catch (nothing is thrown) couldn't stop it from spamming on each render.
   * We resolve where the run lives by silently probing — first as a top-level
   * run (`artboard.textRun`), then nested under each candidate path
   * (`artboard.textByPath`, e.g. min/max live inside the `MinMax` nested
   * artboard) — cache that, then only ever set on confirmed placements. The
   * probe re-runs while nothing has matched yet (a run can be un-queryable for
   * the first frame or two after load) so we never lock in an early miss — that
   * was an accidental property of the old per-call brute force, and dropping it
   * left min/max blank on the first render.
   */
  setTextRunBroadcast(name: string, value: string): void {
    let entry = this.textRunTargets.get(name);
    if (!entry || (entry.placements.length === 0 && entry.tries < RiveLayer.RESOLVE_TRIES)) {
      const placements: TextRunPlacement[] = [];
      for (const key of this.targets.keys()) {
        if (this.hasTextRun(key, name)) {
          placements.push({ key });
          continue;
        }
        for (const path of RiveLayer.TEXT_RUN_PATHS) {
          if (this.hasTextRunAtPath(key, name, path)) {
            placements.push({ key, path });
            break; // first matching path per target is enough
          }
        }
      }
      entry = { placements, tries: (entry?.tries ?? 0) + 1 };
      this.textRunTargets.set(name, entry);
    }
    for (const p of entry.placements) {
      const inst = this.targets.get(p.key)?.instance;
      if (!inst) continue;
      if (p.path) inst.setTextRunValueAtPath(name, value, p.path);
      else inst.setTextRunValue(name, value);
    }
  }

  /**
   * Balance text — Flutter writes `TextInputs.balance` ('txtBalance')
   * on the main artboard (rive_controller_manager.dart:590-592).
   */
  setBalanceText(value: string): void {
    this.setTextRunBroadcast('txtBalance', value);
    // Some .riv variants use these alt names — try them too.
    this.setTextRunBroadcast('txtBalanceAmount', value);
    this.setTextRunBroadcast('txtCredit', value);
  }

  /** Bet-amount text — txtBetAmount on main artboard. */
  setBetAmountText(value: string): void {
    this.setTextRunBroadcast('txtBetAmount', value);
    this.setTextRunBroadcast('txtBet', value);
  }

  /**
   * Drive the `IsSoundOn` boolean input on every artboard that exposes it
   * (present on dragontiger.riv's main + popup artboards). This gates whether
   * the .riv emits its sound-cue events; we keep it true so those cues always
   * reach SoundManager. No-ops on artboards without the input.
   */
  setSoundOn(on: boolean): void {
    for (const key of this.targets.keys()) {
      this.setBoolAny(key, 'IsSoundOn', on);
    }
  }

  /** Win-amount text — txtWinAmount on main artboard. */
  setWinAmountText(value: string): void {
    this.setTextRunBroadcast('txtWinAmount', value);
    this.setTextRunBroadcast('txtWin', value);
  }

  /**
   * History icons. Mirrors the fallback-heavy lookup chain in
   *   rive_controller_manager.dart:304-330
   * which tries multiple name/path combinations because different .riv
   * revisions moved the history inputs around.
   *
   * The web runtime differs from Flutter in one crucial way: Flutter's
   * `sm.number(name, path:)` returns null silently on a miss, but the web
   * runtime's `setNumberStateAtPath` routes through `retrieveInputAtPath`,
   * which `console.warn`s ("Could not access an input…") on every miss and
   * returns void (no success signal). Brute-forcing the candidate matrix
   * through that setter therefore floods the console and can't tell which
   * combination actually worked.
   *
   * So instead we resolve the scheme ONCE via the artboard's lower-level
   * `inputByPath` (which returns falsy silently — see hasInputAtPath), cache
   * it, and from then on only ever call the setter on the confirmed input.
   *
   *  - Index 1..10 (10 most recent rounds)
   *  - Value 0=hidden, 1=Dragon, 2=Tiger, 3=Tie
   */
  setHistory(index: number, value: 0 | 1 | 2 | 3): void {
    if (index < 1 || index > 10) return;

    // Resolve lazily, and keep retrying while unresolved (a nested input may
    // not be queryable for the first frame or two after load — don't lock in
    // that early miss).
    if (this.historyScheme == null && this.historyTries < RiveLayer.RESOLVE_TRIES) {
      // Targets not loaded yet — leave unresolved so we retry next round.
      if (!this.targets.has('main') && !this.targets.has('popup')) return;
      this.historyTries++;
      this.historyScheme = this.resolveHistoryScheme(index);
      if (this.historyScheme === null && this.historyTries >= RiveLayer.RESOLVE_TRIES) {
        debugLog('[Rive] setHistory: no compatible history input found in any .riv');
      }
    }

    const s = this.historyScheme;
    if (!s) return;
    if (s.path) {
      this.setNumberAtPath(s.target, s.name(index), value, s.path(index));
    } else {
      this.setNumberAny(s.target, s.name(index), value);
    }
  }

  /**
   * Resolve which name/path combination the live .riv uses for history slots,
   * probing silently so no "Could not access an input" warnings ever fire.
   * Returns null if nothing matches (history simply stays hidden, as before).
   */
  private resolveHistoryScheme(index: number): HistoryScheme | null {
    const NAME_TEMPLATES: Array<(i: number) => string> = [
      (i) => `TIEBaricon${i}`,
      (i) => `TIEBarIcon${i}`,
      (i) => `TBaricon${i}`,
      (i) => `historyIcon${i}`,
      (i) => `History${i}`,
    ];
    const PATHS = ['result', 'Result', 'history', 'History', 'bar'];
    const targets: TargetKey[] = ['main', 'popup'];

    for (const target of targets) {
      const t = this.targets.get(target);
      if (!t) continue;
      // 1) Top-level cached input (rarely present — kept for completeness).
      for (const tpl of NAME_TEMPLATES) {
        if (t.inputs.get(tpl(index))?.type === 'number') {
          return { target, name: tpl };
        }
      }
      // 2) Nested input at a path, in either orientation (some exports flip
      //    the name and path).
      for (const tpl of NAME_TEMPLATES) {
        for (const p of PATHS) {
          if (this.hasInputAtPath(target, tpl(index), p)) {
            return { target, name: tpl, path: () => p };
          }
          if (this.hasInputAtPath(target, p, tpl(index))) {
            return { target, name: () => p, path: tpl };
          }
        }
      }
    }
    return null;
  }

  /** Replace all 10 history slots; pads empties with 0. */
  setAllHistory(results: ReadonlyArray<1 | 2 | 3>): void {
    for (let i = 0; i < 10; i++) {
      const v = (i < results.length ? results[i] : 0) as 0 | 1 | 2 | 3;
      this.setHistory(i + 1, v);
    }
  }

  clearAllHistory(): void {
    for (let i = 1; i <= 10; i++) this.setHistory(i, 0);
  }

  /**
   * Min/max bet table-limit text — Flutter manager tries multiple names.
   * Routed through the cached broadcast so missing candidates resolve once
   * and never warn (see setTextRunBroadcast).
   */
  setBetLimits(minText: string, maxText: string): void {
    const minCandidates = ['txtMinValue', 'txtMinBet', 'txtMin', 'txtMinLimit'];
    const maxCandidates = ['txtMaxValue', 'txtMaxBet', 'txtMax', 'txtMaxLimit'];
    for (const n of minCandidates) this.setTextRunBroadcast(n, minText);
    for (const n of maxCandidates) this.setTextRunBroadcast(n, maxText);
  }

  // -----------------------------------------------------------------
  // Pub/sub
  // -----------------------------------------------------------------

  on(topic: RiveEventTopic | string, cb: RiveEventCallback): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set();
      this.listeners.set(topic, set);
    }
    set.add(cb);
    return () => set!.delete(cb);
  }

  private emit(topic: string, data: Record<string, unknown>): void {
    // Pass `ready` to the typed `'ready'` listeners only.
    const set = this.listeners.get(topic);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(data);
      } catch (e) {
        warn('[Rive listener]', e);
      }
    }
  }

  // -----------------------------------------------------------------
  // Low-level input plumbing
  // -----------------------------------------------------------------

  private setNumberAny(key: TargetKey, name: string, value: number): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'number') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not number`);
      return false;
    }
    debugLog(`[Rive] number '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private setBoolAny(key: TargetKey, name: string, value: boolean): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'boolean') {
      inp.set(value);
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not boolean`);
      return false;
    }
    debugLog(`[Rive] bool '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  private fireTriggerAny(key: TargetKey, name: string): boolean {
    const t = this.targets.get(key);
    if (!t) return false;
    const inp = t.inputs.get(name);
    if (inp?.type === 'trigger') {
      inp.fire();
      return true;
    }
    if (inp) {
      debugLog(`[Rive] '${name}' on ${key} is ${inp.type}, not trigger`);
      return false;
    }
    debugLog(`[Rive] trigger '${name}' not found on ${key} (${t.file})`);
    return false;
  }

  /** Nested-input setter (Rive WebGL2 calls these the "at path" APIs). */
  private setNumberAtPath(
    key: TargetKey,
    inputName: string,
    value: number,
    path: string,
  ): void {
    const t = this.targets.get(key);
    if (!t) return;
    try {
      t.instance.setNumberStateAtPath(inputName, value, path);
    } catch (e) {
      debugLog(
        `[Rive] setNumberStateAtPath('${inputName}', ${value}, '${path}') failed on ${key}:`,
        e,
      );
    }
  }

  /**
   * Silently check whether an input exists at a nested path, WITHOUT touching
   * `setNumberStateAtPath` (which would `console.warn` on a miss). We reach
   * past the public API to the artboard's `inputByPath`, the same call the
   * runtime's `retrieveInputAtPath` makes — except `inputByPath` returns falsy
   * silently on a miss; only its wrapper adds the warning. Returns false on any
   * runtime that doesn't expose the artboard or the method.
   */
  private hasInputAtPath(key: TargetKey, name: string, path: string): boolean {
    const t = this.targets.get(key);
    const artboard = (
      t?.instance as unknown as {
        artboard?: { inputByPath?: (n: string, p: string) => unknown };
      }
    )?.artboard;
    if (!artboard || typeof artboard.inputByPath !== 'function') return false;
    try {
      return !!artboard.inputByPath(name, path);
    } catch {
      return false;
    }
  }

  /**
   * Silently check whether an artboard exposes a queryable text run, WITHOUT
   * triggering `setTextRunValue`'s `console.warn` on a miss.
   *
   * Primary path: the artboard's own `textRun` — the same call the runtime's
   * `retrieveTextRun` makes, except it returns falsy silently; only the wrapper
   * warns. Fallback (if the artboard isn't directly reachable on some runtime
   * build): the public `getTextRunValue`, run with the warning suppressed. Both
   * resolve through the same `artboard.textRun`, so the answer is guaranteed
   * consistent with what `setTextRunValue` would do.
   */
  private hasTextRun(key: TargetKey, name: string): boolean {
    const inst = this.targets.get(key)?.instance;
    if (!inst) return false;
    const artboard = (inst as unknown as { artboard?: { textRun?: (n: string) => unknown } })
      .artboard;
    if (artboard && typeof artboard.textRun === 'function') {
      try {
        return !!artboard.textRun(name);
      } catch {
        /* fall through to the public-API probe */
      }
    }
    return RiveLayer.silenceRiveAccessWarnings(() => {
      try {
        return inst.getTextRunValue(name) !== undefined;
      } catch {
        return false;
      }
    });
  }

  /**
   * Silently check whether a text run exists nested under `path` (a nested
   * artboard instance name). Primary path: the artboard's own `textByPath`
   * (returns falsy silently). Fallback: the public `getTextRunValueAtPath`
   * under warning suppression. Mirrors hasTextRun for the path-based APIs.
   */
  private hasTextRunAtPath(key: TargetKey, name: string, path: string): boolean {
    const inst = this.targets.get(key)?.instance;
    if (!inst) return false;
    const artboard = (
      inst as unknown as { artboard?: { textByPath?: (n: string, p: string) => unknown } }
    ).artboard;
    if (artboard && typeof artboard.textByPath === 'function') {
      try {
        return !!artboard.textByPath(name, path);
      } catch {
        /* fall through to the public-API probe */
      }
    }
    return RiveLayer.silenceRiveAccessWarnings(() => {
      try {
        return inst.getTextRunValueAtPath(name, path) !== undefined;
      } catch {
        return false;
      }
    });
  }

  /**
   * Run `fn` with Rive's input/text "could not access/get/set … / tried to
   * access …" console warnings suppressed (restoring `console.warn`
   * afterwards). Used only around existence probes so a missing input/run never
   * reaches the console.
   */
  private static silenceRiveAccessWarnings<T>(fn: () => T): T {
    const original = console.warn;
    console.warn = (...args: unknown[]): void => {
      const first = typeof args[0] === 'string' ? args[0] : '';
      if (/^(Could not (access|get|set)|Tried to access)\b/.test(first)) return;
      (original as (...a: unknown[]) => void).apply(console, args);
    };
    try {
      return fn();
    } finally {
      console.warn = original;
    }
  }
}

// eslint hint
export const _UNUSED_RiveEventNames = RiveEventNames;
